// frontend/src/pages/ModuleDetail.tsx
import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Clock, Volume2, VolumeX, ChevronDown, Play, Pause } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import HeyGenAgent from '../components/HeyGenAgent';

declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady: () => void;
  }
}

interface ModuleDetail {
  id: number;
  titre: string;
  description: string;
  video_url: string;
  duree_minutes: number;
  status: string;
}

type YtQuality =
  | 'auto'
  | 'highres'
  | 'hd2880'
  | 'hd2160'
  | 'hd1440'
  | 'hd1080'
  | 'hd720'
  | 'large'
  | 'medium'
  | 'small'
  | string;

export default function ModuleDetail() {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [module, setModule] = useState<ModuleDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [promotion, setPromotion] = useState<any>(null);

  // Player state
  const playerRef = useRef<any>(null);
  const playerMountRef = useRef<HTMLDivElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showCurtain, setShowCurtain] = useState(true); // masque anti "More videos"
  const [volume, setVolume] = useState(80);
  const [muted, setMuted] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const progressTimer = useRef<number | null>(null);

  // Qualité (menu custom – caché sur mobile)
  const [qualityLevels, setQualityLevels] = useState<YtQuality[]>([]);
  const [selectedQuality, setSelectedQuality] = useState<YtQuality>('auto');
  const [qualityOpen, setQualityOpen] = useState(false);
  const qualityRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!qualityRef.current) return;
      if (!qualityRef.current.contains(e.target as Node)) setQualityOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const startTimeRef = useRef<number>(Date.now());
  const searchParams = new URLSearchParams(location.search);
  const promotionId = searchParams.get('promotionId');

  useEffect(() => {
    fetchModuleDetail();
    startTimeRef.current = Date.now();
    if (promotionId) {
      startModule();
      fetchPromotion();
    }
  }, [moduleId]); // eslint-disable-line

  const fetchModuleDetail = async () => {
    try {
      const modulesRes = await api.get(`/modules/promotion/${promotionId}`);
      const currentModule = modulesRes.data.find((m: any) => m.id === parseInt(moduleId!));
      if (currentModule) setModule(currentModule);
    } catch (error) {
      console.error('Erreur lors du chargement du module:', error);
      toast.error('Impossible de charger le module');
    } finally {
      setLoading(false);
    }
  };

  const startModule = async () => {
    try {
      await api.post(`/modules/${moduleId}/start`, null, {
        params: { promotion_id: promotionId }
      });
    } catch (error) {
      console.error('Erreur lors du démarrage du module:', error);
    }
  };

  const fetchPromotion = async () => {
    try {
      const promotionsRes = await api.get('/promotions/my-promotions');
      const currentPromotion = promotionsRes.data.find(
        (p: any) => p.id === parseInt(promotionId!)
      );
      if (currentPromotion) setPromotion(currentPromotion);
    } catch (error) {
      console.error('Erreur lors du chargement de la promotion:', error);
    }
  };

  const completeModule = async () => {
    const totalTime = module
      ? module.duree_minutes * 60
      : Math.floor((Date.now() - startTimeRef.current) / 1000);
    try {
      await api.post(`/modules/${moduleId}/complete`, null, {
        params: { promotion_id: promotionId, time_spent: totalTime }
      });
      toast.success('Module terminé !');
      navigate(`/promotions/${promotionId}/modules`);
    } catch (error) {
      console.error('Erreur lors de la completion du module:', error);
      toast.error('Erreur lors de la validation du module');
    }
  };

  const extractYoutubeId = (url: string): string | null => {
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/);
    return match ? match[1] : null;
  };

  const youtubeId = module?.video_url ? extractYoutubeId(module.video_url) : null;

  useEffect(() => {
    if (!youtubeId) return;
    let cancelled = false;

    const ensureScript = () =>
      new Promise<void>((resolve) => {
        if (window.YT && window.YT.Player) return resolve();
        const existing = document.getElementById('youtube-iframe-api');
        if (existing) {
          const check = setInterval(() => {
            if (window.YT && window.YT.Player) { clearInterval(check); resolve(); }
          }, 50);
          return;
        }
        const tag = document.createElement('script');
        tag.id = 'youtube-iframe-api';
        tag.src = 'https://www.youtube.com/iframe_api';
        document.body.appendChild(tag);
        window.onYouTubeIframeAPIReady = () => resolve();
      });

    const initPlayer = async () => {
      await ensureScript();
      if (cancelled || !playerMountRef.current || !youtubeId) return;

      if (playerRef.current?.destroy) { try { playerRef.current.destroy(); } catch {} }

      playerRef.current = new window.YT.Player(playerMountRef.current, {
        host: 'https://www.youtube-nocookie.com',
        videoId: youtubeId,
        playerVars: {
          controls: 0,
          modestbranding: 1,
          rel: 0,
          fs: 0,
          iv_load_policy: 3,
          cc_load_policy: 0,
          disablekb: 1,
          playsinline: 1,
        },
        events: {
          onReady: (e: any) => {
            try { e.target.unMute?.(); } catch {}
            try { e.target.setVolume(volume); } catch {}
            try { setMuted(!!e.target.isMuted?.()); } catch {}
            try { setDuration(e.target.getDuration?.() || 0); } catch {}
            if (progressTimer.current) window.clearInterval(progressTimer.current);
            progressTimer.current = window.setInterval(() => {
              try { setCurrentTime(e.target.getCurrentTime?.() || 0); } catch {}
            }, 250);

            try {
              const lvls: string[] = e.target.getAvailableQualityLevels?.() || [];
              const uniq = Array.from(new Set(['auto', ...lvls])) as YtQuality[];
              setQualityLevels(uniq);
            } catch {}

            if (selectedQuality !== 'auto') {
              try { e.target.setPlaybackQuality(selectedQuality); } catch {}
            }

            setShowCurtain(true);
          },
          onStateChange: (e: any) => {
            const playing = e.data === 1;
            setIsPlaying(playing);
            setShowCurtain(!playing);
            if (!playing && selectedQuality !== 'auto') {
              try { e.target.setPlaybackQuality(selectedQuality); } catch {}
            }
          },
          onPlaybackQualityChange: () => {
            try {
              const lvls: string[] = playerRef.current?.getAvailableQualityLevels?.() || [];
              const uniq = Array.from(new Set(['auto', ...lvls])) as YtQuality[];
              setQualityLevels(uniq);
            } catch {}
          }
        }
      });
    };

    initPlayer();

    return () => {
      cancelled = true;
      if (progressTimer.current) window.clearInterval(progressTimer.current);
      if (playerRef.current?.destroy) { try { playerRef.current.destroy(); } catch {} }
    };
  }, [youtubeId]); // eslint-disable-line

  // --- Tap fiable (Pointer Events) ---
  const tapState = useRef<{ id: number; x: number; y: number; t: number } | null>(null);
  const TAP_MS = 300;
  const TAP_MOVE = 10;

  const onOverlayPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!e.isPrimary) return;
    tapState.current = { id: e.pointerId, x: e.clientX, y: e.clientY, t: Date.now() };
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
  };

  const onOverlayPointerUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    const s = tapState.current;
    if (!s || e.pointerId !== s.id) return;
    const dx = e.clientX - s.x;
    const dy = e.clientY - s.y;
    const dt = Date.now() - s.t;
    (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
    tapState.current = null;
    if (Math.hypot(dx, dy) < TAP_MOVE && dt < TAP_MS) {
      e.preventDefault();
      e.stopPropagation();
      togglePlayPause();
    }
  };

  const onOverlayPointerCancel = () => { tapState.current = null; };

  // Interactions
  const togglePlayPause = () => {
    if (!playerRef.current) return;
    const state = playerRef.current.getPlayerState?.();
    if (state === 1) playerRef.current.pauseVideo();
    else playerRef.current.playVideo();
  };

  const toggleMute = () => {
    if (!playerRef.current) return;
    try {
      const isM = playerRef.current.isMuted?.();
      if (isM) {
        playerRef.current.unMute?.();
        setMuted(false);
      } else {
        playerRef.current.mute?.();
        setMuted(true);
      }
    } catch {}
  };

  const rewind10 = () => {
    if (!playerRef.current) return;
    try {
      const t = playerRef.current.getCurrentTime?.() || 0;
      playerRef.current.seekTo(Math.max(0, t - 10), true);
    } catch {}
  };
  const forward10 = () => {
    if (!playerRef.current) return;
    try {
      const t = playerRef.current.getCurrentTime?.() || 0;
      const d = playerRef.current.getDuration?.() || 0;
      playerRef.current.seekTo(Math.min(d, t + 10), true);
    } catch {}
  };

  const onProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!playerRef.current || !duration) return;
    const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    const t = Math.max(0, Math.min(duration, ratio * duration));
    try { playerRef.current.seekTo(t, true); } catch {}
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = parseInt(e.target.value, 10);
    setVolume(v);
    try { playerRef.current?.setVolume(v); } catch {}
    if (v === 0 && !muted) { try { playerRef.current?.mute(); } catch {} setMuted(true); }
    if (v > 0 && muted) { try { playerRef.current?.unMute(); } catch {} setMuted(false); }
  };

  const handleQualityPick = (q: YtQuality) => {
    setSelectedQuality(q);
    setQualityOpen(false);
    if (playerRef.current && q !== 'auto') {
      try { playerRef.current.setPlaybackQuality(q); } catch {}
    }
  };

  const fmt = (s: number) => {
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = Math.floor(s % 60);
    return h > 0 ? `${h}:${m.toString().padStart(2,'0')}:${sec.toString().padStart(2,'0')}`
                 : `${m}:${sec.toString().padStart(2,'0')}`;
  };

  const labelForQuality = (q: YtQuality) => {
    const map: Record<string, string> = {
      auto: 'Auto',
      highres: '8K',
      hd2880: '5K',
      hd2160: '4K',
      hd1440: '1440p',
      hd1080: '1080p',
      hd720: '720p',
      large: '480p',
      medium: '360p',
      small: '240p',
    };
    return map[q] || q;
  };

  const order = ['highres','hd2880','hd2160','hd1440','hd1080','hd720','large','medium','small'];
  const sortedQuality = ['auto', ...qualityLevels.filter(q => q !== 'auto')
    .sort((a, b) => order.indexOf(a) - order.indexOf(b))] as YtQuality[];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  if (!module) return <div>Module non trouvé</div>;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <HeyGenAgent heygenAgentUrl={promotion?.product?.heygen_agent_id} />

      {/* En-tête */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <button
          onClick={() => navigate(`/promotions/${promotionId}/modules`)}
          className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-3 sm:mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Retour aux modules
        </button>

        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 break-words">{module.titre}</h1>
        {module.description && <p className="mt-2 text-gray-600 break-words">{module.description}</p>}

        <div className="flex flex-wrap items-center gap-3 sm:gap-4 mt-3 sm:mt-4 text-sm text-gray-500">
          <div className="flex items-center">
            <Clock className="h-4 w-4 mr-1" />
            Durée estimée: {module.duree_minutes} minutes
          </div>
          {module.status === 'completed' && (
            <div className="flex items-center text-green-600">
              <CheckCircle className="h-4 w-4 mr-1" />
              Module terminé
            </div>
          )}
        </div>
      </div>

      {/* Vidéo */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        {youtubeId ? (
          <div className="relative aspect-video w-full rounded-lg overflow-hidden bg-black group">
            {/* Iframe YouTube */}
            <div ref={playerMountRef} className="w-full h-full" />

            {/* --- Bouclier HAUT discret (dégradé, capte clics) --- */}
            <div
              className="absolute top-0 left-0 right-0 z-[36]"
              style={{
                height: '64px',
                background: 'linear-gradient(to bottom, rgba(0,0,0,0.28), rgba(0,0,0,0.10), rgba(0,0,0,0))',
                WebkitTapHighlightColor: 'transparent',
                touchAction: 'none'
              }}
              onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onPointerUp={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
              aria-hidden
            />

            {/* --- Bouclier BAS invisible (capte clics, pas visible) --- */}
            <div
              className="absolute left-0 right-0 bottom-0 z-[36]"
              style={{
                height: '96px',           // assez haut pour couvrir les suggestions YouTube
                background: 'transparent', // totalement invisible
                WebkitTapHighlightColor: 'transparent',
                touchAction: 'none'
              }}
              onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onPointerUp={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
              aria-hidden
            />

            {/* Rideau quand pause/stop (pour ne rien voir de YouTube) */}
            {showCurtain && (
              <>
                <div className="absolute inset-0 z-[28] pointer-events-none bg-black/55 backdrop-blur-sm transition-opacity" />
                <div className="absolute inset-0 z-[29] flex items-center justify-center pointer-events-none">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/15 backdrop-blur-md ring-1 ring-white/30 flex items-center justify-center">
                    <span className="text-white text-3xl leading-none">▶</span>
                  </div>
                </div>
              </>
            )}

            {/* Overlay de contrôle (play/pause au tap) */}
            <button
              type="button"
              className="absolute inset-0 z-[30]"
              onPointerDown={onOverlayPointerDown}
              onPointerUp={onOverlayPointerUp}
              onPointerCancel={onOverlayPointerCancel}
              aria-label={isPlaying ? 'Mettre en pause' : 'Lecture'}
              style={{ WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}
            />

            {/* Masques MOBILE (garde un look YouTube propre) */}
            <div className="absolute top-0 left-0 right-0 h-12 bg-gradient-to-b from-black/60 to-transparent z-[26] sm:hidden pointer-events-none" />
            <div
              className="absolute bottom-0 left-0 right-0 z-[26] sm:hidden pointer-events-none"
              style={{
                height: '28%',
                background: 'linear-gradient(to top, rgba(0,0,0,0.55), rgba(0,0,0,0.0))',
                paddingBottom: 'env(safe-area-inset-bottom)',
              }}
            />

            {/* Barre de progression */}
            <div
              className="absolute left-0 right-0 bottom-0 h-2 cursor-pointer z-40"
              onClick={onProgressClick}
              role="slider"
              aria-valuemin={0}
              aria-valuemax={duration || 0}
              aria-valuenow={currentTime || 0}
              aria-label="Se positionner dans la vidéo"
            >
              <div className="h-full bg-black/40">
                <div
                  className="h-full bg-red-600"
                  style={{ width: duration ? `${(currentTime / duration) * 100}%` : '0%' }}
                />
              </div>
            </div>

            {/* HUD */}
            <div
              className="absolute left-2 right-2 bottom-3 sm:left-3 sm:right-3 sm:bottom-4 z-40 flex items-center justify-between gap-2 sm:gap-3"
              style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
            >
              {/* GAUCHE */}
              <div className="hidden sm:flex items-center gap-2">
                <button
                  onClick={rewind10}
                  className="px-2 py-1 rounded-md bg-black/70 text-white text-xs hover:bg-black/80"
                  title="Reculer 10 s"
                >
                  ⟲ 10s
                </button>
                <button
                  onClick={forward10}
                  className="px-2 py-1 rounded-md bg-black/70 text-white text-xs hover:bg-black/80"
                  title="Avancer 10 s"
                >
                  10s ⟳
                </button>
                <span className="ml-2 text-white text-xs bg-black/60 rounded px-2 py-0.5">
                  {fmt(currentTime)} / {fmt(duration)}
                </span>
              </div>
              <div className="flex sm:hidden items-center">
                <span className="text-white text-[11px] bg-black/60 rounded px-1.5 py-0.5">
                  {fmt(currentTime)} / {fmt(duration)}
                </span>
              </div>

              {/* DROITE */}
              <div className="flex items-center gap-2 bg-black/70 px-2.5 sm:px-3 py-1.5 rounded-lg">
                {/* Play/Pause — MOBILE */}
                <button
                  type="button"
                  onClick={togglePlayPause}
                  className="flex sm:hidden items-center justify-center w-7 h-7 rounded bg-black/60 border border-white/20"
                  aria-label={isPlaying ? 'Mettre en pause' : 'Lecture'}
                >
                  {isPlaying ? <Pause className="h-4 w-4 text-white" /> : <Play className="h-4 w-4 text-white" />}
                </button>

                {/* Play/Pause — DESKTOP */}
                <button
                  type="button"
                  onClick={togglePlayPause}
                  className="hidden sm:flex items-center justify-center w-8 h-8 rounded bg-black/60 border border-white/20 hover:bg-black/70"
                  aria-label={isPlaying ? 'Mettre en pause' : 'Lecture'}
                  title={isPlaying ? 'Pause' : 'Lecture'}
                >
                  {isPlaying ? <Pause className="h-4 w-4 text-white" /> : <Play className="h-4 w-4 text-white" />}
                </button>

                {/* Qualité : desktop uniquement */}
                <div className="hidden sm:flex items-center">
                  <div className="text-white text-xs mr-1 select-none">Qualité</div>
                  <div className="relative" ref={qualityRef}>
                    <button
                      type="button"
                      onClick={() => setQualityOpen(o => !o)}
                      className="inline-flex items-center gap-1 rounded-md bg-black/50 text-white text-xs px-2 py-1 border border-white/20 hover:bg-black/60"
                      aria-haspopup="listbox"
                      aria-expanded={qualityOpen}
                      aria-label="Changer la qualité"
                    >
                      {labelForQuality(selectedQuality)}
                      <ChevronDown className="h-3 w-3 opacity-80" />
                    </button>

                    {qualityOpen && (
                      <div
                        role="listbox"
                        className="absolute right-0 bottom-8 z-50 min-w-[96px] rounded-md bg-black/85 backdrop-blur border border-white/10 p-1 shadow-lg"
                      >
                        {sortedQuality.map((q) => (
                          <button
                            key={q}
                            role="option"
                            aria-selected={q === selectedQuality}
                            onClick={() => handleQualityPick(q)}
                            className={`w-full text-left text-xs px-2 py-1 rounded
                              ${q === selectedQuality ? 'bg-white/20 text-white' : 'text-white/90 hover:bg-white/10'}
                            `}
                          >
                            {labelForQuality(q)}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Volume: slider desktop, bouton mute mobile */}
                <div className="hidden sm:flex items-center">
                  <div className="w-px h-5 bg-white/20 mx-2" />
                  <Volume2 className="h-4 w-4 text-white" />
                  <input
                    type="range"
                    min={0}
                    max={100}
                    value={volume}
                    onChange={handleVolumeChange}
                    className="w-28 accent-white"
                    aria-label="Volume"
                  />
                </div>

                {/* Mute mobile */}
                <button
                  type="button"
                  onClick={toggleMute}
                  className="flex sm:hidden items-center justify-center w-7 h-7 rounded bg-black/60 border border-white/20"
                  aria-label={muted ? 'Activer le son' : 'Couper le son'}
                >
                  {muted ? <VolumeX className="h-4 w-4 text-white" /> : <Volume2 className="h-4 w-4 text-white" />}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-64 sm:h-80 bg-gray-100 rounded-lg">
            <p className="text-gray-500">URL de vidéo invalide</p>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-6">
          <p className="text-sm text-gray-600">
            Regardez la vidéo complètement avant de marquer le module comme terminé
          </p>
          <button
            onClick={completeModule}
            disabled={module.status === 'completed'}
            className={`w-full sm:w-auto inline-flex justify-center items-center px-6 py-3 border border-transparent text-base font-medium rounded-md ${
              module.status === 'completed'
                ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                : 'text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary'
            }`}
          >
            {module.status === 'completed' ? (
              <>
                <CheckCircle className="h-5 w-5 mr-2" />
                Module terminé
              </>
            ) : (
              <>Marquer comme terminé</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
